<template>
  <LoadingDiv v-if="!isReady" />

  <template v-else>
    <section class="invoice-workspace">
      <div class="invoice-head">
        <div>
          <label class="active teal-text">Invoices</label>
          <h5>Invoice Cycles</h5>
        </div>
      </div>

      <div class="invoice-list">
        <div
          v-for="(invoice, index) in invoices"
          :key="invoice.id"
          class="invoice-card"
          :class="statusClass(invoice, index)"
          v-show="currInvoice == invoice || !currInvoice"
        >
          <div class="invoice-main">
            <div class="invoice-title-row">
              <span class="status-chip">
                <i class="material-icons">{{ statusIcon(invoice, index) }}</i>
                {{ statusLabel(invoice, index) }}
              </span>
              <span class="invoice-period">
                {{ formatDate(invoice.start) }} - {{ formatDate(invoice.end) }}
              </span>
            </div>

            <div class="invoice-metrics">
              <div class="metric-primary">
                <span>This Installment</span>
                <strong>NGN {{ changeToMoneyValue(invoice.installment_total) }}</strong>
              </div>
              <div class="metric-secondary">
                <span>Total Premium</span>
                <strong>NGN {{ changeToMoneyValue(invoice.policy_premium_total) }}</strong>
              </div>
              <div class="metric-secondary">
                <span>Total Received</span>
                <strong>
                  NGN {{ changeToMoneyValue(invoice.amount_paid_to_date) }}
                  <em>{{ receivedPercent(invoice) }}%</em>
                </strong>
              </div>
            </div>
          </div>

          <div class="invoice-action">
            <a
              v-if="!currInvoice"
              href="#"
              class="icon-action"
              @click.prevent="handleView(invoice)"
            >
              <i class="material-icons" title="view details">chevron_right</i>
            </a>
            <a
              v-else
              href="#"
              class="icon-action"
              @click.prevent="handleClear()"
            >
              <i class="material-icons" title="go back">keyboard_return</i>
            </a>
          </div>
        </div>
      </div>

      <InvoiceDiv :currInvoice="currInvoice" v-if="currInvoice" />

      <template v-else>
        <div class="empty-state" v-if="!invoices.length">
          <i class="material-icons">receipt_long</i>
          <h6>No invoices found</h6>
        </div>

        <PaginationDiv :payLoad="allPages" @newPage="handlePage" v-else />
      </template>
    </section>
  </template>
</template>

<script>
import { computed, onMounted, ref } from "vue";
import { changeToMoney } from "@/composables/fxLibrary/coreFx";
import getItemsOfItems from "@/composables/getItemsOfItems";
import InvoiceDiv from "@/components/invoice/InvoiceDiv.vue";
import PaginationDiv from "@/components/pager/PaginationDiv.vue";
import LoadingDiv from "@/components/LoadingDiv.vue";

export default {
  name: "PaymentClientComponent",
  components: { LoadingDiv, PaginationDiv, InvoiceDiv },
  props: ["cat", "clientId"],
  setup(props) {
    const response = ref(null);
    const isReady = ref(false);
    const error = ref(null);
    const currInvoice = ref(false);
    const currUrl = ref(null);
    const currHook = ref(null);

    const getItemsFromApi = (table, id) => {
      (async () => {
        const { res, err } = await getItemsOfItems(table, id);

        if (res.value) {
          response.value = res.value.data;
        } else {
          error.value = err.value;
        }

        isReady.value = true;
      })();
    };

    const changeToMoneyValue = (val) => {
      const { fx } = changeToMoney(val || 0);
      return fx.value;
    };

    const receivedPercent = (invoice) => {
      const totalPremium = Number(invoice.policy_premium_total || 0);
      const received = Number(invoice.amount_paid_to_date || 0);

      if (!totalPremium) {
        return 0;
      }

      return Math.round((received / totalPremium) * 100);
    };

    const formatDate = (date) => {
      return new Date(date).toLocaleString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    };

    const statusLabel = (invoice, index) => {
      if (invoice.status == "p" && index == 0) return "Next Pending Invoice";
      if (invoice.status == "p") return "Pending Invoice";
      if (invoice.status == "z") return "Unpaid Grace Period";
      if (invoice.status == "c") return "Completed Payment";
      if (invoice.status == "d") return "Deleted Invoice";
      return "Invoice";
    };

    const statusIcon = (invoice, index) => {
      if (invoice.status == "p" && index == 0) return "schedule";
      if (invoice.status == "p") return "pending_actions";
      if (invoice.status == "z") return "warning";
      if (invoice.status == "c") return "check_circle";
      if (invoice.status == "d") return "delete";
      return "receipt_long";
    };

    const statusClass = (invoice, index) => {
      return {
        "status-next": invoice.status == "p" && index == 0,
        "status-pending": invoice.status == "p" && index > 0,
        "status-grace": invoice.status == "z",
        "status-paid": invoice.status == "c",
        "status-deleted": invoice.status == "d",
      };
    };

    onMounted(() => {
      getInvoices();
    });

    const getInvoices = () => {
      isReady.value = false;
      currUrl.value = "group/invoices/search";
      currHook.value = "?";
      getItemsFromApi(currUrl.value, props.clientId);
    };

    const handlePage = (page) => {
      isReady.value = false;
      getItemsFromApi(
        currUrl.value,
        `${props.clientId}${currHook.value}page=${page}`
      );
    };

    const handleClear = () => {
      currInvoice.value = null;
    };

    const handleView = (val) => {
      currInvoice.value = val;
    };

    const invoices = computed(() => {
      if (!response.value) {
        return [];
      } else {
        return response.value.data;
      }
    });

    const allPages = computed(() => {
      if (!response.value) {
        return [];
      } else {
        return response.value;
      }
    });

    return {
      allPages,
      changeToMoneyValue,
      currInvoice,
      error,
      formatDate,
      handleClear,
      handlePage,
      handleView,
      invoices,
      isReady,
      receivedPercent,
      statusClass,
      statusIcon,
      statusLabel,
    };
  },
};
</script>

<style scoped>
.invoice-workspace {
  margin-top: 0;
}

.invoice-head {
  align-items: center;
  background: #ffffff;
  border: 1px solid #c4d3d9;
  border-radius: 6px;
  box-shadow: 0 14px 24px -28px rgba(38, 50, 56, 0.72);
  display: flex;
  justify-content: space-between;
  margin-bottom: 12px;
  padding: 24px;
}

.invoice-head h5 {
  color: #263238;
  font-weight: 700;
  margin: 4px 0 0;
}

.invoice-list {
  display: grid;
  gap: 12px;
}

.invoice-card {
  align-items: stretch;
  background: #ffffff;
  border: 1px solid #c4d3d9;
  border-radius: 6px;
  box-shadow: 0 14px 24px -28px rgba(38, 50, 56, 0.72);
  display: grid;
  gap: 14px;
  grid-template-columns: minmax(0, 1fr) auto;
  padding: 20px 22px;
  position: relative;
  transition: border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease;
}

.invoice-card:hover {
  border-color: #80cbc4;
  box-shadow: 0 16px 26px -26px rgba(0, 105, 92, 0.62);
  transform: translateY(-1px);
}

.status-next .status-chip {
  background: #ffebee;
  border-color: #ffcdd2;
  color: #b71c1c;
}

.status-next .status-chip i {
  color: #c62828;
}

.status-pending .status-chip {
  background: #f8fafb;
  border-color: #d5e1e5;
  color: #455a64;
}

.status-pending .status-chip i {
  color: #607d8b;
}

.status-grace .status-chip {
  background: #e3f2fd;
  border-color: #bbdefb;
  color: #0d47a1;
}

.status-grace .status-chip i {
  color: #1565c0;
}

.status-paid .status-chip {
  background: #e8f5e9;
  border-color: #c8e6c9;
  color: #1b5e20;
}

.status-paid .status-chip i {
  color: #2e7d32;
}

.status-deleted .status-chip {
  background: #f5f5f5;
  border-color: #e0e0e0;
  color: #616161;
}

.status-deleted .status-chip i {
  color: #757575;
}

.invoice-title-row {
  align-items: center;
  border-bottom: 1px solid #d5e1e5;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: space-between;
  padding-bottom: 12px;
}

.invoice-period {
  background: #f8fafb;
  border: 1px solid #d5e1e5;
  border-radius: 999px;
  color: #607d8b;
  font-size: 0.9rem;
  font-weight: 600;
  padding: 6px 10px;
}

.invoice-metrics {
  display: grid;
  gap: 10px;
  grid-template-columns: minmax(160px, 1.35fr) repeat(2, minmax(120px, 0.75fr));
  margin-top: 14px;
}

.metric-primary,
.metric-secondary {
  background: #fbfcfc;
  border: 1px solid #d5e1e5;
  border-radius: 6px;
  padding: 12px 14px;
}

.metric-primary {
  background: #ffffff;
  border-color: #c4d3d9;
}

.metric-primary span,
.metric-secondary span {
  color: #78909c;
  display: block;
  font-size: 0.78rem;
  font-weight: 700;
  margin-bottom: 4px;
}

.metric-primary strong,
.metric-secondary strong {
  color: #263238;
  display: block;
  font-weight: 700;
}

.metric-primary strong {
  font-size: 1.18rem;
}

.metric-secondary strong {
  font-size: 0.95rem;
}

.metric-secondary em {
  background: #eceff1;
  border-radius: 14px;
  color: #607d8b;
  font-size: 0.78rem;
  font-style: normal;
  margin-left: 6px;
  padding: 3px 7px;
}

.status-chip {
  align-items: center;
  background: #fafafa;
  border: 1px solid #d5e1e5;
  border-radius: 16px;
  color: #607d8b;
  display: inline-flex;
  font-size: 0.86rem;
  font-weight: 700;
  gap: 6px;
  padding: 5px 10px;
}

.status-chip i {
  color: #90a4ae;
  font-size: 18px;
}

.icon-action {
  align-items: center;
  background: #fafafa;
  border: 1px solid #d5e1e5;
  border-radius: 50%;
  color: #607d8b;
  display: inline-flex;
  height: 38px;
  justify-content: center;
  width: 38px;
}

.icon-action i {
  font-size: 21px;
}

.invoice-action {
  align-items: center;
  display: flex;
}

.invoice-card:hover .icon-action {
  background: #eceff1;
  border-color: #b4d6d2;
  color: #263238;
}

.empty-state {
  background: #ffffff;
  border: 1px solid #c4d3d9;
  border-radius: 6px;
  box-shadow: 0 14px 24px -28px rgba(38, 50, 56, 0.72);
  color: #607d8b;
  padding: 30px 16px;
  text-align: center;
}

.empty-state i {
  color: #90a4ae;
  font-size: 34px;
}

.empty-state h6 {
  color: #263238;
  font-weight: 700;
  margin: 8px 0 0;
}

@media (max-width: 900px) {
  .invoice-metrics {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 700px) {
  .invoice-card {
    grid-template-columns: minmax(0, 1fr);
  }

  .invoice-action {
    grid-column: 1;
    justify-content: flex-end;
  }
}
</style>
